import React, { useState, useEffect } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { wallets } from '../services/api';

interface Wallet {
    wallets_id: number;
    name: string;
    balance: number;
}

export const WalletList: React.FC = () => {
    const [walletsList, setWalletsList] = useState<Wallet[]>([]);
    const [open, setOpen] = useState(false);
    const [editingWallet, setEditingWallet] = useState<Wallet | null>(null);
    const [walletName, setWalletName] = useState('');

    useEffect(() => {
        loadWallets();
    }, []);

    const loadWallets = async () => {
        try {
            const response = await wallets.getAll();
            setWalletsList(response.data);
        } catch (error) {
            console.error('Failed to load wallets:', error);
        }
    };

    const handleOpen = (wallet?: Wallet) => {
        if (wallet) {
            setEditingWallet(wallet);
            setWalletName(wallet.name);
        } else {
            setEditingWallet(null);
            setWalletName('');
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingWallet(null);
        setWalletName('');
    };

    const handleSubmit = async () => {
        try {
            if (editingWallet) {
                await wallets.update(editingWallet.wallets_id, { name: walletName });
            } else {
                await wallets.create({ name: walletName });
            }
            handleClose();
            loadWallets();
        } catch (error) {
            console.error('Failed to save wallet:', error);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this wallet?')) {
            try {
                await wallets.delete(id);
                loadWallets();
            } catch (error) {
                console.error('Failed to delete wallet:', error);
            }
        }
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
                sx={{
                    mb: 3,
                    textTransform: 'none',
                    borderRadius: '13px',
                }}
            >
                Add Wallet
            </Button>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {walletsList.map((wallet) => (
                    <Card key={wallet.wallets_id} sx={{ borderRadius: '13px', boxShadow: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                <IconButton color="primary" sx={{ padding: 0, mr: 1 }}>
                                    <AccountBalanceWalletIcon fontSize="large" />
                                </IconButton>
                                {wallet.name}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        backgroundColor: "#e0f7fa",
                                        padding: "6px 12px",
                                        borderRadius: "12px",
                                        fontWeight: "bold",
                                        textAlign: "center",
                                    }}
                                >
                                    RM {wallet.balance}
                                </Typography>
                            </Box>

                            <IconButton
                                onClick={() => handleOpen(wallet)}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                <EditIcon />
                                Edit
                            </IconButton>
                            <IconButton
                                onClick={() => handleDelete(wallet.wallets_id)}
                                size="small"
                                color="error"
                            >
                                <DeleteIcon />
                                Delete
                            </IconButton>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Dialog open={open} onClose={handleClose} sx={{ '& .MuiDialog-paper': { borderRadius: '17px' } }}>
                <DialogTitle>
                    {editingWallet ? 'Edit Wallet' : 'Add New Wallet'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Wallet Name"
                        fullWidth
                        value={walletName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '13px' } }}
                        onChange={(e) => setWalletName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '13px', textTransform: 'none', py: 1.2 }} >
                        {editingWallet ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
